import React from "react";
import { Helmet } from "react-helmet-async";
import "react-loading-skeleton/dist/skeleton.css";
import { Link } from "react-router-dom";
import "./AboutUs.css";

/* ─── Small inline icons (no external icon-pack dependency) ────────────── */
const IconTarget = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
  </svg>
);
const IconLayers = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
  </svg>
);
const IconClipboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="4" rx="1" /><path d="M9 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-4" />
    <line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="15" y2="16" />
  </svg>
);
const IconCode = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
  </svg>
);
const IconMic = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);
const IconCheckCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const IconZap = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconBarChart = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);
const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

const CATEGORIES = [
  { to: "/blogs/all-blogs/mcqs", icon: <IconClipboard />, title: "MCQ Practice", desc: "Topic-wise multiple choice questions with instant answers." },
  { to: "/online-test-series", icon: <IconLayers />, title: "Test Series", desc: "Full-length mock tests with real exam-style scoring." },
  { to: "/blogs/all-blogs/posts", icon: <IconTarget />, title: "Blog Posts", desc: "General knowledge, current affairs & exam-focused reading." },
  { to: "/blogs/all-blogs/technical_posts", icon: <IconCode />, title: "Technical Posts", desc: "Programming languages, databases & core CS concepts." },
  { to: "/blogs/all-blogs/interview_questions", icon: <IconMic />, title: "Interview Questions", desc: "Curated Q&A to help you prepare for real interviews." },
];

const VALUES = [
  { icon: <IconCheckCircle />, title: "Practice-first approach", desc: "Every topic is backed by real questions, not just theory — so you learn by doing." },
  { icon: <IconZap />, title: "Instant results", desc: "No waiting around. Get your score, correct answers, and explanations the moment you submit." },
  { icon: <IconBarChart />, title: "Track your progress", desc: "Detailed performance analytics and all-India rank comparison show you exactly where you stand." },
];

const AboutUs = () => {
  return (
    <>
      <Helmet>
        <title>Acadebility - About Us</title>
        <meta name="description" content="Learn about Acadebility — an online learning platform helping students prepare through mock tests, MCQ practice, interview prep and expert content." />
      </Helmet>

      <div className="lgl-page">
        {/* ── Hero ── */}
        <div className="lgl-hero">
          <div className="lgl-hero-inner">
            <nav className="lgl-breadcrumb">
              <Link to="/">Home</Link>
              <span className="lgl-sep">/</span>
              <span className="lgl-crumb-current">About Us</span>
            </nav>
            <h1 className="lgl-title">About Acadebility</h1>
            <p className="lgl-subtitle">
              Academics + Ability — a platform built to help students and aspirants prepare smarter, practice more, and track real progress along the way.
            </p>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="lgl-body">
          <div className="lgl-body-inner">
            <div className="lgl-layout">

              <main className="lgl-content">

                <section id="who-we-are">
                  <h2>Who we are</h2>
                  <p className="lgl-lead">
                    Acadebility (Academics + Ability) gives you the ability to enhance your academics — a single platform for online education spanning General Knowledge, Current Affairs, Practice Tests, Online Test Series, and Interview Preparation.
                  </p>
                  <p>
                    Whether you're a school student building fundamentals, a competitive exam aspirant preparing for the next milestone, or a job-seeker sharpening interview skills, Acadebility is designed to meet you where you are and help you move forward with focused, structured practice.
                  </p>
                </section>

                <section id="our-mission">
                  <h2>Our mission</h2>
                  <p>
                    Our mission is to build a large, reliable repository of knowledge for students and aspirants — content that's genuinely useful, not just filler. Launched in 2022, Acadebility set out to be a dependable source of high-quality practice material for learners preparing for a wide range of examinations.
                  </p>
                  <div className="lgl-value-grid">
                    {VALUES.map(v => (
                      <div className="lgl-value-card" key={v.title}>
                        <div className="lgl-value-icon">{v.icon}</div>
                        <h4>{v.title}</h4>
                        <p>{v.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="what-we-offer">
                  <h2>What we offer</h2>
                  <p>Every category below is built to be practical first — pick one and start practicing right away.</p>
                  <div className="lgl-cards-grid">
                    {CATEGORIES.map(c => (
                      <Link to={c.to} className="lgl-category-card" key={c.title}>
                        <div className="lgl-category-icon">{c.icon}</div>
                        <h4>{c.title}</h4>
                        <p>{c.desc}</p>
                        <span className="lgl-card-arrow">Explore <IconArrowRight /></span>
                      </Link>
                    ))}
                  </div>
                </section>

                <div className="lgl-cta">
                  <div>
                    <h3>Ready to start practicing?</h3>
                    <p>Jump into a free MCQ set or a full mock test — no long setup, just start.</p>
                  </div>
                  <Link to="/blogs/all-blogs/mcqs" className="lgl-cta-btn">
                    Start practicing <IconArrowRight />
                  </Link>
                </div>

              </main>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUs;